import { useMemo, useState } from "react";

import { Button } from "@/design-system";
import { useScreenTitle } from "@/lib/use-screen-title";
import { useSignupMutation } from "@/api/queries";

import { AuthLayout } from "./AuthLayout";
import { Field } from "./Field";

export interface SignupProps {
  onBack: () => void;
  onLogin: () => void;
  onSignedUp: (email: string) => void;
}

const PASSWORD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,128}$/;

export function Signup({ onBack, onLogin, onSignedUp }: SignupProps) {
  useScreenTitle("Crea il tuo account");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [gdpr, setGdpr] = useState(false);
  const [adult, setAdult] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const signup = useSignupMutation();

  const passwordError = useMemo(() => {
    if (!submitted || password === "") return null;
    if (!PASSWORD_RE.test(password))
      return "Almeno 8 caratteri, una lettera e un numero.";
    return null;
  }, [password, submitted]);

  const confirmError = useMemo(() => {
    if (!submitted || confirm === "") return null;
    if (confirm !== password) return "Le due password non coincidono.";
    return null;
  }, [confirm, password, submitted]);

  const formError = signup.isError
    ? mapSignupError(signup.error)
    : null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !PASSWORD_RE.test(password) ||
      password !== confirm ||
      !gdpr ||
      !adult
    ) {
      return;
    }
    signup.mutate(
      {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
        password,
        password_confirm: confirm,
        gdpr_accepted: true,
        adult_declared: true,
      },
      { onSuccess: () => onSignedUp(email.trim().toLowerCase()) },
    );
  }

  return (
    <AuthLayout
      title="Crea il tuo account"
      subtitle="Bastano email e password. MIMIR farà il resto durante la conversazione."
      onBack={onBack}
      footer={
        <span>
          Hai già un account?{" "}
          <button
            type="button"
            onClick={onLogin}
            className="border-b border-dotted border-ink-faint bg-transparent border-x-0 border-t-0 cursor-pointer text-ink"
          >
            Accedi
          </button>
        </span>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Nome"
            autoComplete="given-name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
          />
          <Field
            label="Cognome"
            autoComplete="family-name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
          />
        </div>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="Almeno 8 caratteri, una lettera e un numero."
          error={passwordError}
          required
        />
        <Field
          label="Conferma password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          error={confirmError}
          required
        />
        <label className="flex items-start gap-3 mb-5 cursor-pointer">
          <input
            type="checkbox"
            checked={gdpr}
            onChange={(e) => setGdpr(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-rame"
            aria-invalid={submitted && !gdpr ? "true" : undefined}
          />
          <span className="text-[13px] leading-[1.5] text-ink-muted">
            Accetto la{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink underline decoration-dotted"
            >
              Privacy Policy
            </a>{" "}
            e i{" "}
            <a
              href="/termini"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ink underline decoration-dotted"
            >
              Termini di servizio
            </a>
            . Acconsento al trattamento dei miei dati clinici per costruire una
            raccomandazione personalizzata.
          </span>
        </label>
        {submitted && !gdpr && (
          <p className="text-[12.5px] text-brick-danger -mt-3 mb-3">
            Per proseguire devi accettare l&apos;informativa.
          </p>
        )}
        <label className="flex items-start gap-3 mb-5 cursor-pointer">
          <input
            type="checkbox"
            checked={adult}
            onChange={(e) => setAdult(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-rame"
            aria-invalid={submitted && !adult ? "true" : undefined}
          />
          <span className="text-[13px] leading-[1.5] text-ink-muted">
            Dichiaro di avere almeno 18 anni. InCorpoSano è riservato ai
            maggiorenni: per i minori è necessario rivolgersi al medico o al
            pediatra.
          </span>
        </label>
        {submitted && !adult && (
          <p className="text-[12.5px] text-brick-danger -mt-3 mb-3">
            Per proseguire devi dichiarare di essere maggiorenne.
          </p>
        )}
        {formError && (
          <p className="text-[13px] text-brick-danger mb-3">{formError}</p>
        )}
        <Button
          type="submit"
          variant="primary"
          full
          disabled={signup.isPending}
        >
          {signup.isPending ? "Creazione…" : "Crea account"}
        </Button>
      </form>
    </AuthLayout>
  );
}

function mapSignupError(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg.includes("409") || msg.includes("email_already_registered"))
    return "Esiste già un account con questa email.";
  if (msg.includes("password_must_have"))
    return "La password non rispetta i requisiti minimi.";
  if (msg.includes("adult_declaration_required"))
    return "Per proseguire devi dichiarare di essere maggiorenne.";
  return "Qualcosa è andato storto. Riprova fra qualche istante.";
}
